import { CancelToken } from 'app/utils/cancelToken';
import { IContextAdapter } from 'app/utils/createServiceContext';

type OnError = (error: unknown) => void;

export default class SubscribableState<StateType> {
  private internalState: StateType;
  private onListenerError: OnError;

  private listeners: ReadonlyArray<() => void> = [];

  /**
   * @param {StateType} init - The initial state value
   * @param {Function} [onListenerError] - Will be called if a listener callback throws an error. If
   * none is provided, will simply re-throw the error to the caller of `setState()`.
   */
  public constructor(
    initialState: StateType,
    options: { onListenerError?: OnError } = {},
  ) {
    this.internalState = initialState;
    this.onListenerError = options?.onListenerError ?? ((error) => { throw error; });
  }

  /**
   * Calls `callback` when the state is updated via `setState`
   *
   * @returns {Function} a function that unsubscribes the given subscriber
   */
  public subscribe(callback: () => void): () => void {
    this.listeners = [...this.listeners, callback];

    const unsubscribe = () => {
      this.listeners = this.listeners.filter(other => other !== callback);
    };

    return unsubscribe;
  }

  public get state(): StateType {
    return this.internalState;
  }

  public setState(
    nextProperties: Partial<StateType>,
    options: {
      skipNotify?: boolean,
    } = {},
  ) {
    const nextState = {
      ...this.internalState,
      ...nextProperties,
    };

    this.setStateNoMerge(nextState, options);
  }

  public setStateNoMerge(
    nextState: StateType,
    options: {
      skipNotify?: boolean,
    } = {},
  ) {
    const {
      skipNotify = false,
    } = options;

    this.internalState = nextState;

    // If skipNotify is true, we don't notify the listeners of this state update.
    if (!skipNotify) {
      // Notify listeners
      //
      // this.listeners is a readonly array, so we can take a snapshot by just
      // holding a reference to its current state
      const listenersSnapshot = this.listeners;
      for (const listener of listenersSnapshot) {
        try {
          listener();
        } catch (error) {
          this.onListenerError(error);
        }
      }
    }
  }
}

/**
 * Resolves when the given predicate function returns true.
 *
 * The predicate function will only be called when the given SubscribableState
 * instance's state changes.
 */
export function waitUntil(
  store: SubscribableState<unknown>,
  predicate: () => boolean,
  cancelToken?: CancelToken,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (predicate()) {
      resolve();
      return;
    }

    const unsubscribe = store.subscribe(() => {
      try {
        if (predicate()) {
          resolve();
          unsubscribe();
        }
      } catch (error) {
        reject(error);
        unsubscribe();
      }
    });

    if (cancelToken) {
      cancelToken.promise.then(reason => {
        reject(reason);
        unsubscribe();
      });
    }
  });
}

/**
 * Creates a pair of get/set functions for some state, and notifies the
 * listeners of the given SubscribableState object whenever the state changes.
 */
export function bindState<T>(
  store: SubscribableState<unknown>,
  initialValue: T,
) {
  let value: T = initialValue;

  const get = (): T => value;
  const set = (nextValue: T): void => {
    value = nextValue;

    // Dirty hack? Use a no-op setState() to notify listeners without actually
    // changing the SubscribableState's state.
    store.setStateNoMerge(store.state);
  };

  return [get, set] as const;
}

/**
 * Useful when you want to keep a value in a SubscribableState, but don't want
 * updating that value to trigger the SubscribableState's listeners. Similar in
 * spirit to React's `useRef`, but uses getter and setter functions instead of a
 * `.current` property to avoid eslint from complaining about assigning values
 * to a parameter (for when the SubscribableState object is passed in as a
 * parameter to a helper function).
 */
export function makeVolatileValue<T>(initialValue: T): VolatileValue<T> {
  let value = initialValue;

  return {
    get: () => value,
    set: (nextValue) => {
      value = nextValue;
    },
  };
}
export type VolatileValue<T> = {
  get: () => T,
  set: (nextValue: T) => void,
};

/**
 * Arguments for using createServiceContext with a SubscribableState instance.
 */
export const subscribableStateAdapter: IContextAdapter<SubscribableState<unknown>> = {
  getVersion: (service) => service.state,
  subscribe: (service, callback: () => void) => service.subscribe(callback),
};
